import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Input, Skeleton } from "antd5";

import Button from "components/ui/button/Button";
import PaywallStar from "lib/icons/PaywallStar";
import { createUseDebounce } from "../../../../lib/debounce";
import UIcon from "../../../../lib/icons/untitled_ui/UIcon";
import {
  sysBorderDecorative,
  sysPrimaryDefault,
  sysPrimarySubtle,
} from "../../../../lib/themes/colors";
import { Flex, Text } from "../../../../styles/utility-components";
import EmptyState from "../../../ui/empty_state/EmptyState";
import { ListEntities } from "./SaveToLists";

export type Item = {
  label: string;
  isSaved: boolean;
  id: string;
  viewUrl: string;
  paywall?: boolean;
};

export type CreateListFormProps = {
  onCancel: () => void;
  onSuccess: () => void;
  footer: React.ReactNode;
  entityIds: string[];
};

export type LoadingState = {
  lists?: boolean;
  itemId?: string;
};

type Props = {
  entityIds: string[];
  entityType: ListEntities;
  items: Item[];
  onSearch: (searchValue: string) => void;
  onItemClick: (id: string, isSaved: boolean) => void;
  loading: LoadingState;
  isError: boolean;
  CreateListForm?: React.ComponentType<CreateListFormProps>;
  isCreateFormOpen: boolean;
  onToggleCreateForm: (isCreateFormOpen: boolean) => void;
};

const useDebounce = createUseDebounce(500);

export function SaveToList({
  onSearch,
  CreateListForm,
  items,
  onItemClick,
  loading,
  isError,
  entityIds,
  isCreateFormOpen: isCreatingList,
  onToggleCreateForm: setIsCreatingList,
}: Props) {
  const [searchText, setSearchText] = useState("");
  const [hasInitialItems, setHasInitialItems] = useState(false);

  const onSearchDebounced = useDebounce((text: string) => {
    onSearch(text);
  });

  useEffect(() => {
    if (!hasInitialItems && items.length > 0) {
      setHasInitialItems(!!items.length);
    }
  }, [items, hasInitialItems]);

  if (isCreatingList) {
    return (
      <Flex column gap={16} height={"100%"} flexGrow={1}>
        <Text h2>Create new list</Text>
        {CreateListForm && (
          <CreateListForm
            onCancel={() => setIsCreatingList(false)}
            onSuccess={() => setIsCreatingList(false)}
            footer={
              <Flex column gap={8} flexGrow={1} justifyContent="flex-end">
                <Button type="submit" aria-label="create-list-button">
                  Create new list
                </Button>
              </Flex>
            }
            entityIds={entityIds}
          />
        )}
      </Flex>
    );
  }

  return (
    <Flex column gap={16} height={"100%"} aria-label="Save to list content">
      {hasInitialItems && (
        <Input
          placeholder="Search"
          prefix={<UIcon icon="searchRefraction" size={16} />}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            onSearchDebounced(e.target.value);
          }}
          allowClear
        />
      )}
      <ListOfLists items={items} loading={loading} isError={isError} onItemClick={onItemClick} />
      {CreateListForm && <Button onClick={() => setIsCreatingList(true)}>Create new list</Button>}
    </Flex>
  );
}

const BookmarkIcon = styled(UIcon)({
  display: "flex",
  alignItems: "center",
  marginLeft: "8px",
});

function ListOfLists({
  items,
  loading,
  isError,
  onItemClick,
}: {
  items: Item[];
  onItemClick: (id: string, isSaved: boolean) => void;
  loading: LoadingState;
  isError: boolean;
}) {
  if (loading.lists) {
    return (
      <ListContainer>
        <Skeleton active />
      </ListContainer>
    );
  }

  if (isError) {
    return (
      <EmptyState
        type="loadError"
        heading="Sorry something has gone wrong"
        description="If this error persists please contact support"
        width="100%"
        size="large"
      />
    );
  }

  if (items.length === 0) {
    return <EmptyState heading="You haven't added any lists yet" width="100%" size="large" />;
  }

  return (
    <ListContainer>
      {items.map((item) => (
        <ListItem key={item.id}>
          <IconWrapper>
            <UIcon icon="list" size={16} />
          </IconWrapper>
          <ContentFlex>
            <Flex minWidth={0}>
              <Text ellipsis>
                {item.label}
                {item.paywall && <PaywallStar />}
              </Text>
            </Flex>
            {item.isSaved && <BookmarkIcon icon="bookmark" color={sysPrimaryDefault} size={12} />}
          </ContentFlex>
          <ActionsContainer>
            <Button
              icon="bookmark"
              iconProps={{
                size: 16,
                fill: item.isSaved ? "currentColor" : "none",
                color: "currentColor",
              }}
              onClick={() => onItemClick(item.id, item.isSaved)}
              loading={loading.itemId === item.id}
              aria-label={
                item.isSaved ? `Remove from list ${item.label}` : `Add to list ${item.label}`
              }
            >
              {item.isSaved ? "Unsave" : "Save"}
            </Button>
            <Button as="a" buttonType="secondary" target="_blank" href={item.viewUrl}>
              View
            </Button>
          </ActionsContainer>
        </ListItem>
      ))}
    </ListContainer>
  );
}

const ActionsContainer = styled.div({
  display: "none",
  gap: 4,
  marginLeft: "auto",
});

const ListContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: 8,
  height: "100%",
  overflowY: "auto",
});

const IconWrapper = styled.div({
  display: "flex",
  flex: "0 0 16px",
});

const ContentFlex = styled(Flex)({
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  overflow: "hidden",
  flex: 1,
});

const ListItem = styled.div({
  display: "flex",
  gap: 16,
  height: 54,
  border: "1px solid",
  borderRadius: 4,
  padding: "12px 16px",
  borderColor: sysBorderDecorative,
  alignItems: "center",
  transition: "background-color 140ms ease-out, border-color 140ms ease-out",
  "&:hover": {
    backgroundColor: sysPrimarySubtle,
    borderColor: sysPrimaryDefault,
    [`${ActionsContainer}`]: {
      display: "flex",
    },
    [`${BookmarkIcon}`]: {
      display: "none",
    },
  },
});
